import { createBrowserRouter } from 'react-router'
import { RequireAuth, RequireStaff, RequireAdmin, RequirePlan, RedirectIfAuthed } from './routes/guards'
import { JudgmentBoundary } from './lib/JudgmentBoundary'
import { AppShell } from './layouts/AppShell'
import Login from './routes/Login'
import Home from './routes/Home'
import Records from './routes/Records'
import Settings from './routes/Settings'
import ManualEntry from './routes/ManualEntry'
import Leave from './routes/Leave'
import LeaveRequest from './routes/LeaveRequest'
import Contracts from './routes/Contracts'
import Payslips from './routes/Payslips'
import Trainings from './routes/Trainings'
import Dashboard from './routes/admin/Dashboard'
import AdminAttendance from './routes/admin/Attendance'
import Approvals from './routes/admin/Approvals'
import Untagged from './routes/admin/Untagged'
import Employees from './routes/admin/Employees'
import EmployeeDetail from './routes/admin/EmployeeDetail'
import Departments from './routes/admin/Departments'
import NfcSpots from './routes/admin/NfcSpots'
import NfcMappings from './routes/admin/NfcMappings'
import AdminSettings from './routes/admin/Settings'
import LeaveBalances from './routes/admin/LeaveBalances'
import LeaveApprovals from './routes/admin/LeaveApprovals'
import AdminContracts from './routes/admin/Contracts'
import AdminPayslips from './routes/admin/Payslips'
import AdminTrainings from './routes/admin/Trainings'
import ChainVerify from './routes/admin/security/ChainVerify'
import Anomalies from './routes/admin/security/Anomalies'
import AuditLogs from './routes/admin/security/AuditLogs'
import DeviceRegister from './routes/onboarding/DeviceRegister'
import Privacy from './routes/onboarding/Privacy'
import ChangePassword from './routes/onboarding/ChangePassword'
import { OnboardingGate } from './routes/onboarding/gate'
import NotFound from './routes/NotFound'

/** 로그인 뒤 화면은 전부 판정 컨텍스트 + 온보딩 게이트 안에 들어간다 (§3-2) */
function Authed({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <OnboardingGate>
        <JudgmentBoundary>{children}</JudgmentBoundary>
      </OnboardingGate>
    </RequireAuth>
  )
}

/** 온보딩은 하단탭 없이 단독 화면 — 끝내야 앱을 쓸 수 있다 */
function Onboarding({ children }: { children: React.ReactNode }) {
  return (
    <Authed>
      <main className="mx-auto w-full max-w-md px-5 py-8">{children}</main>
    </Authed>
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RedirectIfAuthed>
        <Login />
      </RedirectIfAuthed>
    ),
  },
  {
    // 직원 화면과 관리 화면을 나누지 않는다. 하나의 셸 안에서 역할이 보이는 범위를 정한다.
    path: '/',
    element: (
      <Authed>
        <AppShell />
      </Authed>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'records', element: <Records /> },
      { path: 'manual', element: <ManualEntry /> },
      { path: 'leave', element: <RequirePlan min="PRO" what="휴가를 신청하고 남은 연차를 볼 수 있습니다."><Leave /></RequirePlan> },
      { path: 'leave/new', element: <RequirePlan min="PRO" what="휴가를 신청할 수 있습니다."><LeaveRequest /></RequirePlan> },
      { path: 'contracts', element: <RequirePlan min="PRO" what="근로계약서를 앱에서 받고 서명할 수 있습니다."><Contracts /></RequirePlan> },
      { path: 'payslips', element: <RequirePlan min="PRO" what="급여명세서를 본인만 열어볼 수 있습니다."><Payslips /></RequirePlan> },
      { path: 'trainings', element: <RequirePlan min="PRO" what="교육 이수증을 모아 두고 언제든 꺼내 볼 수 있습니다."><Trainings /></RequirePlan> },
      { path: 'settings', element: <Settings /> },
      {
        path: 'admin',
        children: [
          { index: true, element: <RequireStaff><Dashboard /></RequireStaff> },
          { path: 'attendance', element: <RequireStaff><AdminAttendance /></RequireStaff> },
          { path: 'approvals', element: <RequireStaff><Approvals /></RequireStaff> },
          { path: 'untagged', element: <RequireStaff><Untagged /></RequireStaff> },
          { path: 'employees', element: <RequireStaff><Employees /></RequireStaff> },
          // 상세는 부서장도 본다 — 서버가 담당 부서로 scope 를 건다
          { path: 'employees/:id', element: <RequireStaff><EmployeeDetail /></RequireStaff> },
          { path: 'departments', element: <RequireAdmin><Departments /></RequireAdmin> },
          { path: 'nfc-spots', element: <RequireAdmin><NfcSpots /></RequireAdmin> },
          // 담당 근무지 = 대리출근 차단. 서버도 ADMIN 전용
          { path: 'nfc-mappings', element: <RequireAdmin><NfcMappings /></RequireAdmin> },
          // 조직 설정 — 판정 모드를 여기서 고른다. 최종관리자 전용 (서버도 403)
          { path: 'settings', element: <RequireAdmin><AdminSettings /></RequireAdmin> },
          { path: 'leave-approvals', element: <RequireStaff><RequirePlan min="PRO" what="휴가 신청을 받고 승인할 수 있습니다."><LeaveApprovals /></RequirePlan></RequireStaff> },
          { path: 'leave-balances', element: <RequireAdmin><RequirePlan min="PRO" what="연차를 부여하고 남은 일수를 관리할 수 있습니다."><LeaveBalances /></RequirePlan></RequireAdmin> },
          // 발송·회수는 ADMIN 전용(서버도 동일). 부서장은 담당 부서 현황 조회만
          { path: 'contracts', element: <RequireStaff><RequirePlan min="PRO" what="근로계약서를 직원에게 보내고 서명을 받을 수 있습니다."><AdminContracts /></RequirePlan></RequireStaff> },
          // 급여명세서는 부서장도 못 본다 (서버도 ADMIN 전용)
          { path: 'payslips', element: <RequireAdmin><RequirePlan min="PRO" what="급여명세서를 사람별로 나눠 보낼 수 있습니다."><AdminPayslips /></RequirePlan></RequireAdmin> },
          // 교육은 부서장도 본다 — 서버가 담당 부서로 scope 를 건다. 올리기·지우기만 ADMIN
          { path: 'trainings', element: <RequireStaff><RequirePlan min="PRO" what="교육 이수증을 모아 두고 «안 받은 사람» 을 볼 수 있습니다."><AdminTrainings /></RequirePlan></RequireStaff> },
          // 보안 3종은 최종관리자 전용 — 서버도 requireAdmin 이다 (§16-D15)
          { path: 'security/audit-logs', element: <RequireAdmin><AuditLogs /></RequireAdmin> },
          { path: 'security/anomalies', element: <RequireAdmin><Anomalies /></RequireAdmin> },
          { path: 'security/chain-verify', element: <RequireAdmin><ChainVerify /></RequireAdmin> },
        ],
      },
    ],
  },
  {
    // 게이트가 mustChangePassword 인 사람을 여기로 보낸다. 라우트가 없으면 404 에 갇힌다
    path: '/change-password',
    element: (
      <Onboarding>
        <ChangePassword />
      </Onboarding>
    ),
  },
  {
    path: '/onboarding/privacy',
    element: (
      <Onboarding>
        <Privacy />
      </Onboarding>
    ),
  },
  {
    path: '/onboarding/device',
    element: (
      <Onboarding>
        <DeviceRegister />
      </Onboarding>
    ),
  },
  { path: '*', element: <NotFound /> },
], {
  /*
   * 경로 분리 — 이 앱은 `app.biznomi.com/mtt/` 아래에 산다 (2026-09-04).
   *
   * 화면 안의 경로는 전부 `/` 기준 그대로 쓰고, 브라우저 주소만 `/mtt` 가
   * 앞에 붙는다. 그래서 `navigate('/admin/employees')` 같은 코드는 안 고쳐도 된다.
   * `vite.config.ts` 의 `base` 와 이 값은 **같이 움직여야 한다.**
   */
  basename: '/mtt',
})
